import type { FetchOptions } from 'ofetch';
import type { Options as CamelCaseKeysOptions } from 'camelcase-keys';

import { HTTP_DEFAULT_TIMEOUT } from '../constants';

export interface HttpRequestOptions {
  query?: Record<string, string | number | boolean>;
  headers?: Record<string, string>;
  responseType?: 'json' | 'text' | 'blob';
  pathParams?: Record<string, string>;
  timeout?: number;
  /** Convert to camel case json responses key if enabled. Enabled by default. */
  camelCase?: boolean | CamelCaseKeysOptions;
  /** Number of retry attempts, or false to disable retries */
  retry?: number | false;
  /** Delay between retries in milliseconds, or function for dynamic delay (e.g., exponential backoff) */
  retryDelay?: FetchOptions['retryDelay'];
  /** HTTP status codes that should trigger a retry */
  retryStatusCodes?: number[];
}

export interface HttpResponse<T = void> {
  data: T;
  status: number;
  headers?: Record<string, string>;
}

/**
 * Abstract HTTP client interface for making API requests.
 * This allows switching between different HTTP implementations (ofetch, axios, etc.) easily.
 */
export abstract class HttpClient {
  protected baseUrl: string;
  protected defaultHeaders: Record<string, string>;
  protected defaultTimeout: number;
  protected authToken: string | null = null;

  constructor(baseUrl: string, defaultHeaders: Record<string, string> = {}, defaultTimeout: number = HTTP_DEFAULT_TIMEOUT) {
    this.baseUrl = baseUrl;
    this.defaultHeaders = defaultHeaders;
    this.defaultTimeout = defaultTimeout;
  }

  abstract get<T>(path: string, options?: HttpRequestOptions): Promise<HttpResponse<T>>;
  abstract post<T>(path: string, body?: unknown, options?: HttpRequestOptions): Promise<HttpResponse<T>>;
  abstract put<T>(path: string, body?: unknown, options?: HttpRequestOptions): Promise<HttpResponse<T>>;
  abstract delete<T>(path: string, options?: HttpRequestOptions): Promise<HttpResponse<T>>;
  abstract patch<T>(path: string, body?: unknown, options?: HttpRequestOptions): Promise<HttpResponse<T>>;

  /**
   * Set authentication token for all subsequent requests
   */
  setAuthToken(token: string): void {
    this.authToken = token;
  }

  /**
   * Clear authentication token from all subsequent requests
   */
  clearAuthToken(): void {
    this.authToken = null;
  }

  /**
   * Update default headers
   */
  updateDefaultHeaders(headers: Record<string, string>): void {
    this.defaultHeaders = { ...this.defaultHeaders, ...headers };
  }

  /**
   * Get the full URL for a given path
   */
  protected getFullUrl(path: string): string {
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    return `${this.baseUrl}/${normalizedPath}`;
  }
}
